import { useCallback, useEffect, useRef } from "react";
import { type ItineraryService } from "../../../services/ItineraryService";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import {
    type ItineraryDebugRelaunchCallbacks,
    type ItineraryDebugRelaunchDeps,
    relaunchDetailsOffer,
    relaunchDetailsSegmentationAndOffer,
    relaunchListSegmentation,
} from "./itineraryDebugRelaunch.tools";

type OfferFetchStatus = "idle" | "loading" | "success" | "error";

/** Input wiring for {@link useItineraryDebugRelaunch}. */
export interface UseItineraryDebugRelaunchParams {
    "itineraryService":            ItineraryService;
    "pricingPreferences":          ItineraryPricingPreferencesDTO;
    "requestOfferForItinerary":    ( itinerary: ItineraryDTO ) => void;
    "itineraries":                 ItineraryDTO[];
    "selectedItinerary":           ItineraryDTO | null;
    "setItineraries":              ( updater: ItineraryDTO[] | ( ( previous: ItineraryDTO[] ) => ItineraryDTO[] ) ) => void;
    "setSelectedItinerary":        ( updater: ItineraryDTO | null | ( ( previous: ItineraryDTO | null ) => ItineraryDTO | null ) ) => void;
    "setOfferStatusByItineraryId": ( updater: Record<string, OfferFetchStatus> | ( ( previous: Record<string, OfferFetchStatus> ) => Record<string, OfferFetchStatus> ) ) => void;
    "setLoadingPricing":           ( value: boolean ) => void;
    "loadingPricing":              boolean;
    "offerStatusByItineraryId":    Record<string, OfferFetchStatus>;
    "abortInFlightPricing":        () => void;
    "abortInFlightOffers":         ( itineraryIds: readonly string[] ) => void;
    "presentErrorToast":           ( message: string ) => void;
}

/** Relaunch handlers and disabled flags for itinerary debug toolbars. */
export interface UseItineraryDebugRelaunchResult {
    "handleRelaunchListSegmentation":            () => void;
    "handleRelaunchDetailsSegmentationAndOffer": () => void;
    "handleRelaunchDetailsOffer":                () => void;
    "isListActionDisabled":                      boolean;
    "isDetailsActionDisabled":                   boolean;
}

/**
 * Composes itinerary debug relaunch handlers for ItineraryList toolbars.
 *
 * @param params ItineraryList state and service dependencies.
 * @returns Debug relaunch handlers and disabled flags.
 */
export const useItineraryDebugRelaunch = ( params: UseItineraryDebugRelaunchParams ): UseItineraryDebugRelaunchResult => {
    const paramsRef = useRef( params );
    const listRelaunchAbortControllerRef = useRef<AbortController | null>( null );

    useEffect( () => {
        paramsRef.current = params;
    } );

    const buildCallbacks = (): ItineraryDebugRelaunchCallbacks => {
        const currentParams = paramsRef.current;

        return {
            "getItineraries":              (): ItineraryDTO[] => currentParams.itineraries,
            "setItineraries":              currentParams.setItineraries,
            "getSelectedItinerary":        (): ItineraryDTO | null => currentParams.selectedItinerary,
            "setSelectedItinerary":        currentParams.setSelectedItinerary,
            "setOfferStatusByItineraryId": currentParams.setOfferStatusByItineraryId,
            "setLoadingPricing":           currentParams.setLoadingPricing,
            "abortInFlightPricing":        currentParams.abortInFlightPricing,
            "abortInFlightOffers":         currentParams.abortInFlightOffers,
            "presentErrorToast":           currentParams.presentErrorToast,
        };
    };

    const buildDeps = (): ItineraryDebugRelaunchDeps => {
        const currentParams = paramsRef.current;

        return {
            "itineraryService":         currentParams.itineraryService,
            "pricingPreferences":       currentParams.pricingPreferences,
            "requestOfferForItinerary": currentParams.requestOfferForItinerary,
        };
    };

    const selectedItineraryOfferStatus: OfferFetchStatus = params.selectedItinerary !== null
        ? params.offerStatusByItineraryId[params.selectedItinerary.id] ?? "idle"
        : "idle";

    const isListActionDisabled = params.loadingPricing || params.itineraries.length === 0;
    const isDetailsActionDisabled = params.selectedItinerary === null || selectedItineraryOfferStatus === "loading";

    const handleRelaunchListSegmentation = useCallback( (): void => {
        if ( listRelaunchAbortControllerRef.current !== null ) {
            listRelaunchAbortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        listRelaunchAbortControllerRef.current = abortController;

        void relaunchListSegmentation( buildDeps(), buildCallbacks(), abortController.signal ).
            finally( () => {
                if ( listRelaunchAbortControllerRef.current === abortController ) {
                    listRelaunchAbortControllerRef.current = null;
                }
            } );
    }, [] );

    const handleRelaunchDetailsSegmentationAndOffer = useCallback( (): void => {
        relaunchDetailsSegmentationAndOffer( buildDeps(), buildCallbacks() );
    }, [] );

    const handleRelaunchDetailsOffer = useCallback( (): void => {
        relaunchDetailsOffer( buildDeps(), buildCallbacks() );
    }, [] );

    return {
        handleRelaunchListSegmentation,
        handleRelaunchDetailsSegmentationAndOffer,
        handleRelaunchDetailsOffer,
        isListActionDisabled,
        isDetailsActionDisabled,
    };
};
